import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, GripVertical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useTranslation } from 'react-i18next';
import QuickReminderForm from './QuickReminderForm';

interface FabPosition {
  x: number;
  y: number;
}

interface QuickReminderFabProps {
  isVisible: boolean;
  onToggle: () => void;
}

export default function QuickReminderFab({ isVisible, onToggle }: QuickReminderFabProps) {
  const { t } = useTranslation('reminders');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const BOTTOM_BAR_HEIGHT = 130;
  const FAB_SIZE = 56;
  const getDefaultPosition = () => ({
    x: Math.max(16, (typeof window !== 'undefined' ? window.innerWidth : 375) - 80), 
    y: (typeof window !== 'undefined' ? window.innerHeight : 667) - BOTTOM_BAR_HEIGHT - FAB_SIZE - 24
  });
  const [position, setPosition] = useState<FabPosition>(getDefaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState<FabPosition>({ x: 0, y: 0 });
  const fabRef = useRef<HTMLDivElement>(null);

  // Load position from localStorage – validácia, aby FAB nebol pod bottom bar
  useEffect(() => {
    const savedPosition = localStorage.getItem('reminder-fab-position');
    if (savedPosition) {
      try {
        const parsed = JSON.parse(savedPosition);
        const maxY = window.innerHeight - BOTTOM_BAR_HEIGHT - FAB_SIZE - 16;
        if (parsed.y <= maxY && parsed.y >= 0) {
          setPosition(parsed);
        }
      } catch (error) {
        console.error('Error parsing saved FAB position:', error);
      }
    }
  }, []);

  // Save position to localStorage
  const savePosition = (newPosition: FabPosition) => {
    setPosition(newPosition);
    localStorage.setItem('reminder-fab-position', JSON.stringify(newPosition));
  };

  // Handle drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    // Allow dragging from any part of the FAB
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
    e.preventDefault();
  };

  // Handle drag
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;

    const newPosition = {
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    };

    // Constrain to viewport (nad bottom bar, v safe area)
    const maxX = Math.max(0, window.innerWidth - FAB_SIZE - 16);
    const maxY = window.innerHeight - BOTTOM_BAR_HEIGHT - FAB_SIZE - 16;

    newPosition.x = Math.max(0, Math.min(newPosition.x, maxX));
    newPosition.y = Math.max(0, Math.min(newPosition.y, maxY));

    savePosition(newPosition);
  };

  // Handle drag end
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Add global event listeners for drag
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart]);

  // Handle keyboard shortcut Alt+R
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.altKey && e.key === 'r') {
        e.preventDefault();
        setIsFormOpen(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle form submission
  const handleFormSubmit = () => {
    setIsFormOpen(false);
  };

  // Handle form close
  const handleFormClose = () => {
    setIsFormOpen(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      {/* FAB Button */}
      <div
        ref={fabRef}
        className="fixed z-[60] cursor-move select-none"
        style={{
          left: `${position.x}px`,
          top: `${position.y}px`,
        }}
        onMouseDown={handleMouseDown}
      >
        <Button
          size="lg"
          className="h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all duration-200 bg-primary hover:bg-primary/90"
          onClick={() => setIsFormOpen(true)}
          title={t('fab.tooltip')}
        >
          <Plus className="h-6 w-6" />
        </Button>
        
        {/* Drag handle – skryté na mobile, aby nevyzeralo rozpadnuto */}
        <div className="absolute -top-1 -right-1 bg-muted rounded-full p-1 cursor-move hidden sm:block">
          <GripVertical className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>

      {/* Form Modal – safe area a fullscreen na mobile */}
      {isFormOpen && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center bg-black/50 overflow-y-auto p-4 pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
          <div className="min-h-full w-full max-w-md flex items-center justify-center">
            <Card className="w-full max-w-md my-4 sm:my-8">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <CardTitle>{t('form.title', 'Nová pripomienka')}</CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleFormClose}
                >
                  <X className="h-4 w-4" />
                </Button>
              </CardHeader>
              <CardContent className="max-h-[70vh] overflow-y-auto">
                <QuickReminderForm
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormClose}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      )}
    </>
  );
}
